import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import apiService from '../services/api';
import Features from './Features';

const Solutions = () => {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentSolution, setCurrentSolution] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    editable_product_list: 'true'
  });
  const [selectedSolution, setSelectedSolution] = useState(null);

  useEffect(() => {
    fetchSolutions();
  }, []);

  const fetchSolutions = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/solution');
      console.log('Fetched solutions:', response.data);
      setSolutions(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load solutions. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      code: '',
      editable_product_list: ''
    });
  };

  const handleAddSolution = async () => {
    try {
      await apiService.post('/solution', formData);
      fetchSolutions();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to add solution. Please try again.');
      console.error(err);
    }
  };

  const handleEditClick = (solution) => {
    setCurrentSolution(solution);
    setFormData({
      name: solution.name,
      description: solution.description,
      code: solution.code,
      editable_product_list: solution.editable_product_list
    });
    setShowEditModal(true);
  };

  const handleUpdateSolution = async () => {
    try {
      await apiService.put(`/solution/${currentSolution.id}`, formData);
      fetchSolutions();
      setShowEditModal(false);
    } catch (err) {
      setError('Failed to update solution. Please try again.');
      console.error(err);
    }
  };

  if (selectedSolution) {
    return (
      <Features
        solutionId={selectedSolution.id}
        solutionName={selectedSolution.name}
        onBack={() => setSelectedSolution(null)}
      />
    );
  }

  return (
    <div className="p-4">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Solutions</h4>
          <Button variant="primary" onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}>
            <i className="bi bi-plus-circle me-2"></i>Add Solution
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="editable_product_list">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Code</th>
                  <th>Editable Product List</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {solutions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">No solutions found</td>
                  </tr>
                ) : (
                  solutions.map((solution) => (
                    <tr key={solution.id}>
                      <td>{solution.name}</td>
                      <td>{solution.description}</td>
                      <td>{solution.code}</td>
                      <td>
                        <span className={`badge bg-${solution.editable_product_list == true ? 'success' : 'secondary'}`}>
                          {(solution.editable_product_list == true) ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditClick(solution)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setSelectedSolution(solution)}
                        >
                          <i className="bi bi-list-check me-1"></i>Features
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add Solution Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Solution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Code</Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                />
              </Form.Group>
              <Form.Group as={Col}>
                <Form.Label>Editable Product List</Form.Label>
                <Form.Select
                  name="editable_product_list"
                  value={formData.editable_product_list}
                  onChange={handleInputChange}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddSolution}>
            Add Solution
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Solution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Category</Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                />
              </Form.Group>
              <Form.Group as={Col}>
                <Form.Label>Editable Product List</Form.Label>
                <Form.Select
                  name="editable_product_list"
                  value={formData.editable_product_list}
                  onChange={handleInputChange}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateSolution}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Solutions;